import type { LitSquadInventoryEntry, LitSquadInventoryItemDetail } from '@/lib/api';

/**
 * Validates that a URI is a safe HTTP(S) URL suitable for use as an image src.
 */
export function isSafeImageUri(uri: string | null | undefined): boolean {
  if (!uri) return false;
  try {
    const url = new URL(typeof uri === 'string' ? uri.trim() : uri);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

/**
 * Preferred display name for a LitSquad inventory row. Falls back to the
 * species-level definition name when the item has no per-item override
 * (e.g. admin-minted items without a legacy asset_name).
 */
export function resolveLitSquadDisplayName(
  entry: Pick<LitSquadInventoryEntry, 'displayName' | 'speciesName'>,
): string {
  return entry.displayName && entry.displayName.trim().length > 0
    ? entry.displayName
    : entry.speciesName;
}

/**
 * Preferred thumbnail for a LitSquad inventory row — the item-level override
 * (legacy icon rehosted under arche_dfe_icons/LitSquad/) takes precedence
 * over the species-level definition default.
 */
export function resolveLitSquadThumbnail(
  entry: Pick<LitSquadInventoryEntry, 'thumbnailUri' | 'definitionThumbnailUri'>,
): string | null {
  return entry.thumbnailUri ?? entry.definitionThumbnailUri ?? null;
}

export function resolveLitSquadItemDisplayName(
  item: Pick<LitSquadInventoryItemDetail, 'displayName'>,
  speciesName: string | null,
): string {
  if (item.displayName && item.displayName.trim().length > 0) return item.displayName;
  return speciesName ?? 'LitSquad NFT';
}
